/**
 * <CanonicalUrl /> — emits a per-route `<link rel="canonical">` tag.
 *
 * Why this exists
 * ────────────────
 *  The static `index.html` shipped by CRA can only carry ONE canonical
 *  URL — and that fallback points at the homepage. In a SPA every route
 *  shares the same initial HTML, so without a runtime override every
 *  page declares the homepage as its canonical. Google's Search Console
 *  then reports a flurry of:
 *      • "Duplicate without user-selected canonical"
 *      • "Duplicate, Google chose different canonical than user"
 *      • "Page with redirect" (when /foo/ → /foo)
 *
 *  Mounted once at the App level, this component reads the current
 *  pathname, normalises it (strips query, hash, and trailing slashes
 *  except the root) and writes the correct canonical via React Helmet,
 *  which replaces the static fallback.
 *
 *  Per-page Helmet entries (e.g. ResourcePreview) can still emit their
 *  own canonical — Helmet picks the LAST one to render, so a page-level
 *  canonical wins over this default. That gives us a safe global
 *  canonical for every route AND room for hand-tuned overrides where
 *  needed.
 */
import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SITE_ORIGIN = "https://hidroverse.co.za";

function normalisePath(pathname) {
  if (!pathname || pathname === "/") return "/";
  // Strip duplicated slashes and a single trailing slash.
  let clean = pathname.replace(/\/{2,}/g, "/");
  if (clean.length > 1 && clean.endsWith("/")) clean = clean.slice(0, -1);
  return clean;
}

export default function CanonicalUrl() {
  const { pathname } = useLocation();
  const href = `${SITE_ORIGIN}${normalisePath(pathname)}`;
  return (
    <Helmet>
      <link rel="canonical" href={href} />
      <meta property="og:url" content={href} />
    </Helmet>
  );
}
